import React, { useContext } from "react";
import { Stack, TextField } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import { TeamsFxContext } from "../../../components/Context";
import { useTheme } from "../../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../../theme/styles/CommonControlsStyle";
import { useTeamsRequestContext } from "../../../redux/hooks/useTeamsRequestContext";
import { useLogin } from "../../../redux/hooks/useLogin";
import { useMetadata } from "../../../redux/hooks/useMetadata";
import { useDocumentSearch } from "../../../redux/hooks/useDocumentSearch";
import {
  buildRevisionSearchParams,
  generateDocuments,
  parseDocNoFromSearchText,
  findLatestDropdownItemForDocNo,
} from "./searchRevision/revisionSearchResults.utils";
import FieldValidationMessage from "../../../components/checkinDocuments/FieldValidationMessage";

const DEBOUNCE_MS = 350;
const MAX_RESULTS = 10; // Match AddoShare NewRevisionTab (maxResults: 10)

function normalizeInput(value) {
  return (value ?? "").toString();
}

function hasCompleteRevisionSelection(item) {
  return !!(
    item?.class &&
    item?.docNo &&
    item?.docRev &&
    (item?.documentNo || `${item.docNo} - ${item.docRev}`)
  );
}

export default function RevisionDocumentAutocomplete({
  selectedRevisionItem,
  onSelectRevisionItem,
  onClearSelection,
  showFieldValidation = true,
  fieldError = "",
  focusToken = 0,
}) {
  const theme = useTheme();
  const { themeString } = useContext(TeamsFxContext);
  const isDarkMode = themeString === "dark" || themeString === "contrast";
  // Match disabled Title field: `styleTheme.TextFiledsStyle` uses `theme.colors.backgroundColor`.
  const fieldBg = theme.colors.backgroundColor;
  const fieldFg = isDarkMode ? "#FFFFFF" : theme.colors.fontColor;
  const styleTheme = useCommonControlsStyle();
  const { tenantId, groupId: requestGroupId, channelId: requestChannelId } = useTeamsRequestContext();
  const { teamsContext } = useLogin();
  const isGeneralAppContext = !teamsContext?.channel?.id;
  const effectiveChannelId = isGeneralAppContext ? "" : requestChannelId ?? "";
  const effectiveGroupId = isGeneralAppContext ? "" : requestGroupId ?? "";
  const { advanceSearchOnlyUserInvolved, checkedOutToMe } = useMetadata();
  const { searchDocuments, clearSearch, loading, documents, isError, message } = useDocumentSearch();

  const [inputValue, setInputValue] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const debounceRef = React.useRef(null);
  const [dropdownConfirmed, setDropdownConfirmed] = React.useState(false);
  const isPrefilledSelection = hasCompleteRevisionSelection(selectedRevisionItem);
  const hasSelection =
    (dropdownConfirmed || isPrefilledSelection) &&
    !!(
      selectedRevisionItem?.documentNo ||
      (selectedRevisionItem?.docNo && selectedRevisionItem?.docRev)
    );
  const [forceShowDropdown, setForceShowDropdown] = React.useState(false);
  const [isEditingSelectedValue, setIsEditingSelectedValue] = React.useState(false);
  const rootRef = React.useRef(null);
  const textFieldRef = React.useRef(null);
  const lastSelectedDocNoRef = React.useRef("");
  const pendingAutoSelectDocNoRef = React.useRef("");
  const shouldAutoSelectRef = React.useRef(false);

  React.useEffect(() => {
    if (!focusToken) return;
    setIsFocused(true);
    setForceShowDropdown(false);
    setIsEditingSelectedValue(false);
    requestAnimationFrame(() => {
      const inputEl = rootRef.current?.querySelector?.("input");
      inputEl?.focus?.();
      inputEl?.select?.();
    });
  }, [focusToken]);

  const runRevisionSearch = React.useCallback(
    (searchValue) => {
      const docNo = parseDocNoFromSearchText(searchValue);
      if (!tenantId || !docNo) return;
      const params = buildRevisionSearchParams({
        searchValue: docNo,
        pageIndex: 0,
        pageSize: MAX_RESULTS,
        tenantId,
        groupId: effectiveGroupId,
        channelId: effectiveChannelId,
        advanceSearchOnlyUserInvolved,
        checkedOutToMe,
      });
      searchDocuments(params);
    },
    [
      tenantId,
      effectiveGroupId,
      effectiveChannelId,
      advanceSearchOnlyUserInvolved,
      checkedOutToMe,
      searchDocuments,
    ]
  );

  // Reset internal state when the parent clears the selection (e.g. dialog close).
  React.useEffect(() => {
    if (selectedRevisionItem != null) return;
    setInputValue("");
    setDropdownConfirmed(false);
    setIsFocused(false);
    setIsEditingSelectedValue(false);
    pendingAutoSelectDocNoRef.current = "";
    lastSelectedDocNoRef.current = "";
    shouldAutoSelectRef.current = false;
    clearSearch();
  }, [selectedRevisionItem, clearSearch]);

  // External prefill should still search by doc no on load so the field resolves
  // through the same latest-revision dropdown flow as manual search.
  React.useEffect(() => {
    if (!selectedRevisionItem || dropdownConfirmed) return;

    const docNo = parseDocNoFromSearchText(
      selectedRevisionItem.docNo ?? selectedRevisionItem.documentNo ?? ""
    );
    if (!docNo) return;
    if (pendingAutoSelectDocNoRef.current === docNo) return;

    pendingAutoSelectDocNoRef.current = docNo;
    shouldAutoSelectRef.current = true;
    setInputValue(docNo);
    setIsFocused(true);
    runRevisionSearch(docNo);
  }, [
    selectedRevisionItem,
    dropdownConfirmed,
    runRevisionSearch,
  ]);

  // Keep input in sync after a dropdown selection (not stale "docNo - A2" from navigation).
  React.useEffect(() => {
    if (!dropdownConfirmed) return;

    const selectedDisplay = selectedRevisionItem?.documentNo ?? "";
    const normalized = normalizeInput(selectedDisplay);
    const prev = lastSelectedDocNoRef.current;
    lastSelectedDocNoRef.current = normalized;

    if (normalized && normalized !== prev) {
      setInputValue(normalized);
    }
  }, [selectedRevisionItem?.documentNo, dropdownConfirmed]);

  // Debounced search as user types.
  React.useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const q = inputValue.trim();
    const qDocNo = parseDocNoFromSearchText(q);

    // If we already have a selected document and the input matches it, do not re-search.
    // This prevents a brief "Searching..." flash after selecting an item.
    if (
      hasSelection &&
      selectedRevisionItem?.documentNo &&
      qDocNo === parseDocNoFromSearchText(selectedRevisionItem.documentNo) &&
      !shouldAutoSelectRef.current
    ) {
      clearSearch();
      return;
    }

    // If cleared, clear results and selection.
    if (!q) {
      clearSearch();
      // When user enters "edit selected value" mode we intentionally set inputValue=""
      // but we must not clear the selected item yet.
      if (!isEditingSelectedValue) {
        setDropdownConfirmed(false);
        pendingAutoSelectDocNoRef.current = "";
        onClearSelection?.();
      }
      return;
    }

    // If the user is typing over an existing selection, clear selection so they can pick a new doc.
    if (
      hasSelection &&
      selectedRevisionItem?.documentNo &&
      qDocNo !== parseDocNoFromSearchText(selectedRevisionItem.documentNo)
    ) {
      setDropdownConfirmed(false);
      pendingAutoSelectDocNoRef.current = "";
      onClearSelection?.();
    }

    if (!tenantId) return;

    debounceRef.current = setTimeout(() => {
      pendingAutoSelectDocNoRef.current = qDocNo;
      shouldAutoSelectRef.current = false;
      runRevisionSearch(q);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [
    inputValue,
    tenantId,
    effectiveGroupId,
    effectiveChannelId,
    advanceSearchOnlyUserInvolved,
    checkedOutToMe,
    runRevisionSearch,
    clearSearch,
    hasSelection,
    selectedRevisionItem?.documentNo,
    onClearSelection,
    isEditingSelectedValue,
  ]);

  const rawDocs = React.useMemo(
    () => (Array.isArray(documents) ? documents : []),
    [documents]
  );
  const items = React.useMemo(
    () => generateDocuments(rawDocs).slice(0, MAX_RESULTS),
    [rawDocs]
  );

  // Keep a cache of the last non-empty results so the chevron can re-open them after selection.
  const lastItemsRef = React.useRef([]);
  React.useEffect(() => {
    if (Array.isArray(items) && items.length > 0) {
      lastItemsRef.current = items;
    }
  }, [items]);

  const displayItems = React.useMemo(() => {
    if (Array.isArray(items) && items.length > 0) return items;
    return Array.isArray(lastItemsRef.current) ? lastItemsRef.current : [];
  }, [items]);

  const showResults =
    isFocused &&
    !hasSelection &&
    inputValue.trim().length > 0 &&
    !loading &&
    !isError &&
    items.length > 0;

  const showNoResults =
    isFocused &&
    !hasSelection &&
    inputValue.trim().length > 0 &&
    !loading &&
    !isError &&
    items.length === 0;

  const showError =
    isFocused &&
    !hasSelection &&
    inputValue.trim().length > 0 &&
    !loading &&
    isError;

  // AddoShare: when focused but empty, show the placeholder-like message in dropdown.
  const showTypeToSearchPrompt =
    isFocused &&
    !hasSelection &&
    !isEditingSelectedValue &&
    inputValue.trim().length === 0 &&
    !loading &&
    !isError;

  // When a selection exists, allow the user to reopen the last populated results via chevron/click.
  const canShowCachedDropdown =
    hasSelection &&
    isEditingSelectedValue &&
    displayItems.length > 0 &&
    !loading &&
    !isError;

  const isDropdownOpen =
    (!hasSelection && (loading || showResults || showNoResults || showError || showTypeToSearchPrompt)) ||
    canShowCachedDropdown;

  // Close dropdown on outside click (match AddoShare behavior).
  React.useEffect(() => {
    if (!isDropdownOpen) return;

    const onDocMouseDown = (ev) => {
      const el = rootRef.current;
      if (!el) return;
      if (el.contains(ev.target)) return;
      setIsFocused(false);
      setForceShowDropdown(false);
      if (isEditingSelectedValue) {
        setIsEditingSelectedValue(false);
        // Restore selected value visuals once the edit mode closes.
        const selectedDisplay = selectedRevisionItem?.documentNo ?? "";
        setInputValue(normalizeInput(selectedDisplay));
      }
    };

    document.addEventListener("mousedown", onDocMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
    };
  }, [isDropdownOpen, isEditingSelectedValue]);

  const handleSelect = React.useCallback(
    (item) => {
      if (!item) return;
      setIsEditingSelectedValue(false);
      setDropdownConfirmed(true);
      pendingAutoSelectDocNoRef.current = "";
      shouldAutoSelectRef.current = false;
      setInputValue(normalizeInput(item.documentNo ?? ""));
      clearSearch();
      setIsFocused(false);
      setForceShowDropdown(false);
      onSelectRevisionItem?.(item);
      const inputEl = rootRef.current?.querySelector?.("input");
      inputEl?.blur?.();
    },
    [clearSearch, onSelectRevisionItem]
  );

  // Auto-select when dropdown has the latest row for the doc no we searched.
  React.useEffect(() => {
    if (dropdownConfirmed || loading || isError || isEditingSelectedValue) return;
    if (!shouldAutoSelectRef.current) return;

    const docNo =
      pendingAutoSelectDocNoRef.current ||
      parseDocNoFromSearchText(inputValue.trim());
    if (!docNo) return;

    const inputDocNo = parseDocNoFromSearchText(inputValue.trim());
    if (inputDocNo !== docNo) return;

    const match = findLatestDropdownItemForDocNo(items, docNo);
    if (!match) return;

    handleSelect(match);
  }, [
    loading,
    items,
    isError,
    dropdownConfirmed,
    inputValue,
    isEditingSelectedValue,
    handleSelect,
  ]);

  return (
    <Stack.Item grow style={{ marginTop: "10px", width: "100%", position: "relative" }}>
      <div ref={rootRef}>
        <span
          style={{
            fontFamily: theme.fonts.regular,
            fontSize: theme.fontSizes.normal,
          }}
        >
          Document
          <span style={{ color: theme.colors.fontColor }}> *</span>
        </span>

        <div
          style={{ position: "relative" }}
          onMouseDownCapture={(e) => {
            // Fluent TextField icon/chevron clicks don't trigger the input's onClick.
            // Capture clicks anywhere in the field (including icon + chevron) to reopen cached results.
            if (e.target?.closest?.("[data-role='clear-selected-document']")) return;
            if (hasSelection && displayItems.length > 0) {
              setIsFocused(true);
              setForceShowDropdown(true);
              setIsEditingSelectedValue(true);
              setInputValue("");
              const inputEl = rootRef.current?.querySelector?.("input");
              if (inputEl) {
                inputEl.focus();
                requestAnimationFrame(() => {
                  try {
                    // caret at start (empty input)
                    inputEl.setSelectionRange(0, 0);
                  } catch {
                    // ignore
                  }
                });
              } else {
                textFieldRef.current?.focus?.();
              }
            }
          }}
        >
        {hasSelection ? (
          <div
            data-role="clear-selected-document"
            role="button"
            tabIndex={0}
            aria-label="Clear selected document"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              // Ensure clearing doesn't re-open cached dropdown.
              setForceShowDropdown(false);
              setIsFocused(false);
              setDropdownConfirmed(false);
              pendingAutoSelectDocNoRef.current = "";
              shouldAutoSelectRef.current = false;
              onClearSelection?.();
              clearSearch();
              setInputValue("");
            }}
            style={{
              position: "absolute",
              right: 34, // leave space for chevron on the far right
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              zIndex: 2,
            }}
          >
            <Icon
              iconName="Cancel"
              style={{ fontSize: 12, color: fieldFg, opacity: 1 }}
            />
          </div>
        ) : null}

        <TextField
          componentRef={textFieldRef}
          styles={{
            ...styleTheme.TextFiledsStyle,
            // Ensure the chevron icon is vertically centered inside the field.
            icon: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              transform: "translateY(6px)",
            },
            root: {
              ...(styleTheme.TextFiledsStyle?.root ?? {}),
              selectors: {
                ...(styleTheme.TextFiledsStyle?.root?.selectors ?? {}),
                ".ms-TextField-icon": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  lineHeight: "normal",
                  marginTop: 0,
                  marginBottom: 0,
                  transform: "translateY(6px)",
                },
                ".ms-TextField-fieldGroup": {
                  ...(styleTheme.TextFiledsStyle?.root?.selectors?.[".ms-TextField-fieldGroup"] ?? {}),
                  backgroundColor: `${fieldBg} !important`,
                },
                ".ms-TextField-field": {
                  color: `${fieldFg} !important`,
                  backgroundColor: `${fieldBg} !important`,
                },
                ".ms-TextField-field::placeholder": {
                  color: `${fieldFg} !important`,
                  opacity: 0.9,
                  fontSize: "12px",
                },
                ".ms-TextField-placeholder": {
                  color: `${fieldFg} !important`,
                  opacity: 0.9,
                  fontSize: "12px",
                },
              },
            },
            fieldGroup: {
              ...(styleTheme.TextFiledsStyle?.fieldGroup ?? {}),
              paddingRight: hasSelection && !isEditingSelectedValue ? 64 : undefined,
              backgroundColor: `${fieldBg} !important`,
              // Match `TextFiledsStyle` border visuals (Title field).
              border: "1px solid #dcdcdc",
              borderRadius: "0px",
            },
            field: {
              ...(styleTheme.TextFiledsStyle?.field ?? {}),
              color: `${fieldFg} !important`,
              backgroundColor: `${fieldBg} !important`,
            },
          }}
          value={inputValue}
          onChange={(_, v) => {
            setForceShowDropdown(false);
            const next = normalizeInput(v);
            setInputValue(next);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            // Let click selection happen before hiding results.
            setTimeout(() => {
              if (isEditingSelectedValue) return;
              setIsFocused(false);
              setForceShowDropdown(false);
            }, 150);
          }}
          // Note: click handling for chevron/icon is done on wrapper (onMouseDownCapture)
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && items.length > 0) {
              ev.preventDefault();
              const latest = findLatestDropdownItemForDocNo(items, inputValue);
              handleSelect(latest ?? items[0]);
            }
            if (ev.key === "Escape") {
              clearSearch();
              setIsFocused(false);
              setForceShowDropdown(false);
            }
          }}
          placeholder={showTypeToSearchPrompt ? "" : isEditingSelectedValue ? "" : "Type to search documents..."}
          iconProps={{
            iconName: "ChevronDown",
            styles: {
              root: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                lineHeight: "normal",
                transform: "translateY(6px)",
                color: fieldFg,
                opacity: 1,
              },
            },
          }}
        />
        <FieldValidationMessage
          message={showFieldValidation ? fieldError : ""}
        />
      </div>

        {((!hasSelection && (loading || showResults || showNoResults || showError || showTypeToSearchPrompt)) || canShowCachedDropdown) && (
          <div
            style={{
              position: "absolute",
              top: 58,
              left: 0,
              right: 0,
              zIndex: 20,
              background: theme.colors.contextMenuBackground,
              border: `1px solid ${theme.colors.Addovation20Grey}`,
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
          {!hasSelection && showTypeToSearchPrompt && (
            <div
              style={{
                padding: 12,
                fontSize: theme.fontSizes.normal,
                fontFamily: theme.fonts.regular,
                color: theme.colors.fontColor,
                textAlign: "center",
              }}
            >
              Type to search documents...
            </div>
          )}

          {!hasSelection && loading && (
            <div style={{ padding: 12, fontFamily: theme.fonts.regular, color: theme.colors.fontColor, textAlign: "center" }}>
              Searching…
            </div>
          )}

          {!hasSelection && showError && (
            <div style={{ padding: 12, fontFamily: theme.fonts.regular, color: theme.colors.validationColor }}>
              {message || "Search failed"}
            </div>
          )}

          {!hasSelection && showNoResults && (
            <div style={{ padding: 12, fontFamily: theme.fonts.regular, color: theme.colors.fontColor, textAlign: "center" }}>
              No documents found
            </div>
          )}

          {(showResults || canShowCachedDropdown) &&
            displayItems.map((item) => (
              <div
                key={item.key}
                role="button"
                tabIndex={0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
                style={{
                  padding: 10,
                  cursor: "pointer",
                  borderBottom: `1px solid ${theme.colors.Addovation20Grey}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.contextMenuHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {/* Icon column: centered vertically across the full row height */}
                  <div style={{ width: 24, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {item.iconName ? (
                      <img
                        src={item.iconName}
                        alt=""
                        aria-hidden
                        style={{ width: 20, height: 20, objectFit: "contain", flexShrink: 0 }}
                      />
                    ) : (
                      <span style={{ width: 20, height: 20, flexShrink: 0 }} />
                    )}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontFamily: theme.fonts.regular,
                        color: theme.colors.fontColor,
                        fontWeight: 600,
                        fontSize: 13,
                        lineHeight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.documentNo}
                    </div>

                    {item.title ? (
                      <div
                        style={{
                          fontFamily: theme.fonts.regular,
                          color: theme.colors.subTitle,
                          fontSize: 13,
                          lineHeight: "15px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.title}
                      </div>
                    ) : null}

                    <div style={{ fontFamily: theme.fonts.regular, color: theme.colors.subTitle, fontSize: 11, lineHeight: "14px" }}>
                      {item.class
                        ? `Class: ${item.class}${item.className ? "-" + item.className : ""}`
                        : ""}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Stack.Item>
  );
}

